import React, { useEffect, useRef, useCallback, useState } from 'react'
import PropTypes from 'prop-types'

import Checkbox from '../Checkbox'
import DropdownButton from '../DropdownButton'

const TableHeaders = ({ tablePayload, tableElement, activeColumns, UUIDCheckboxes }) => {
  const refs = useRef([])
  const [activeIndex, setActiveIndex] = useState(null)
  const [initialX, setInitialX] = useState(0)
  const [initialWidth, setInitialWidth] = useState(0)
  const [newColWidth, setNewColWidth] = useState(0)

  const allColumns = [
    ...(UUIDCheckboxes ? [
      { // Checkbox column
        name: 'checkbox_column',
        changeSize: false,
        toggleOptions: false,
        checkbox: true,
        sticky: !!tablePayload.stickyColumn
      }
    ] : []),
    { // Sticky column
      name: tablePayload.stickyColumn?.name,
      render: tablePayload.stickyColumn?.render,
      display: tablePayload.stickyColumn?.display,
      minCellWidth: tablePayload.stickyColumn?.minCellWidth,
      toggleOptions: tablePayload.stickyColumn?.toggleOptions,
      changeSize: true,
      sticky: true
    },
    ...activeColumns.map((item) => ({
      minCellWidth: item.minCellWidth ? item.minCellWidth : 75,
      toggleOptions: item.toggleOptions ? item.toggleOptions : true,
      ...item
    }))
  ]

  const onMove = useCallback(
    (e) => {
      e.stopPropagation()
      const gridColumns = refs.current.map((col, i) => {
        if (i === activeIndex) {
          document.body.style.cursor = 'col-resize'
          const newWidth = e.type === 'mousemove'
            ? initialWidth + (e.clientX - initialX)
            : initialWidth + (e.touches[0].clientX - initialX)
          if (newWidth >= allColumns[i].minCellWidth) {
            setNewColWidth(newWidth)
            return `${newWidth}px`
          }
        }
        return `${col ? `${col.offsetWidth}px` : ''}`
      }).filter((d) => d)
      gridColumns[gridColumns.length - 1] = 'auto'
      tableElement.current.style.gridTemplateColumns = `${gridColumns.join(' ')}`
    },
    [activeIndex, allColumns, initialWidth, initialX, tableElement]
  )

  const onMoveEnd = useCallback(() => {
    document.body.style.cursor = 'default'

    // Update schema with new startingWidth
    const newSchema = tablePayload.schema.map((col) => {
      if (col.name !== allColumns[activeIndex].name) return col
      return {
        ...col,
        startingWidth: newColWidth,
        fractionalWidth: 0
      }
    })
    tablePayload.setSchema(newSchema)

    setActiveIndex(null)

    // Remove listeners
    window.removeEventListener('mousemove', onMove)
    window.removeEventListener('mouseup', onMoveEnd)
    window.removeEventListener('touchmove', onMove)
    window.removeEventListener('touchend', onMoveEnd)
  }, [tablePayload, onMove, allColumns, activeIndex, newColWidth])

  useEffect(() => {
    if (activeIndex !== null) {
      window.addEventListener('mousemove', onMove)
      window.addEventListener('mouseup', onMoveEnd)
      window.addEventListener('touchmove', onMove)
      window.addEventListener('touchend', onMoveEnd)
    }
    return () => {
      // Remove listeners
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onMoveEnd)
      window.removeEventListener('touchmove', onMove)
      window.removeEventListener('touchend', onMoveEnd)
    }
  }, [activeIndex, onMove, onMoveEnd])

  const tableHeaderButtons = (d, i) => {
    const isActiveSort = d.name === tablePayload.sortColumn

    return (
      <div className='th-buttons d-flex justify-content-between'>
        {refs.current[i]?.clientWidth >= 100 && (
          <button
            name={d.name}
            type='button'
            className={`btn btn-sm btn-light me-1 ${isActiveSort ? 'active-sort' : ''}`}
            onClick={() => tablePayload.setColumnSortToggle(d.name)}
          >
            <span
              name={d.name}
              className={`fa-solid ${tablePayload.sortOrder === 'asc' ? 'fa-arrow-up' : 'fa-arrow-down'}`}
              aria-label={isActiveSort ? `${tablePayload.sortColumn} Sort Arrow` : 'Sort Arrow'}
              role="img"
            />
          </button>
        )}
        {<DropdownButton
          variant='light'
          size='sm'
          options={[
            {
              content: <span><span className='fas fa-arrow-up me-3'></span>Sort by Ascending</span>,
              disabled: tablePayload.sortOrder === 'asc' && tablePayload.sortColumn === d.name,
              onClick: () => tablePayload.setColumnSortToggle(d.name, 'asc')
            },
            {
              content: <span><span className='fas fa-arrow-down me-3'></span>Sort by Descending</span>,
              disabled: tablePayload.sortOrder === 'desc' && tablePayload.sortColumn === d.name,
              onClick: () => tablePayload.setColumnSortToggle(d.name, 'desc')
            },
            {
              content: <span><span className='fas fa-times me-3'></span>Unsort</span>,
              onClick: () => tablePayload.setColumnSortToggle('', '')
            },
            { divider: true },
            {
              content: <span><span className='fas fa-text-width me-3'></span>Truncate Overflow</span>,
              disabled: !tablePayload.textWrap?.includes(d.name),
              onClick: () => tablePayload.setTextWrap((columns) => columns.filter((x) => x !== d.name))
            },
            {
              content: <span><span className='fas fa-text-height me-3'></span>Wrap Overflow</span>,
              disabled: tablePayload.textWrap?.includes(d.name),
              onClick: () => tablePayload.setTextWrap([...tablePayload.textWrap, d.name])
            },
            { divider: true },
            {
              content: <span><span className='fas fa-arrow-left-to-line me-3'></span>Pin to Left</span>,
              disabled: (tablePayload.stickyColumn?.name === d.name),
              onClick: () => {
                if (tablePayload.stickyColumn?.name === d.name) {
                  tablePayload.setSticky(null)
                } else {
                  tablePayload.setSticky(d)
                }
              }
            },
            {
              content: <span><span className='fas fa-times me-3'></span>Unpin</span>,
              disabled: (tablePayload.stickyColumn?.name !== d.name),
              onClick: () => tablePayload.setSticky(null)
            }
          ]}
        >
          <span className='fa-solid fa-ellipsis'></span>
        </DropdownButton>
        }
      </div>
    )
  }

  return <div className='thead'>
    <div className='tr'>
      {allColumns.map((d, i) => (
        (!d.sticky || tablePayload.stickyColumn) &&
        <div
          key={i}
          className={`d-flex align-items-center ${d.sticky ? 'sticky-col th' : 'th'}`}
          ref={(element) => {
            refs.current[i] = element
          }}
          name={d.name}
          style={{
            boxShadow: d.sticky ? '5px 3px 5px -3px #d4d4d4' : '',
            left: d.name !== 'checkbox_column' && d.sticky
              ? (UUIDCheckboxes ? '45px' : '0')
              : ''
          }}
        >
          <h6
            className='pointer my-1'
            name={d.name}
            onClick={() => tablePayload.setColumnSortToggle(d.name)}
          >
            {d.display}
          </h6>
          {d.toggleOptions && !d.isOptionColumn && !activeIndex && tableHeaderButtons(d, i)}
          {d.checkbox &&
            <div className='w-100 d-flex align-items-center'>
              <Checkbox
                name='select-all'
                onChange={tablePayload.toggleSelectAll}
                checked={tablePayload.isAllSelected}
                isMixed={tablePayload.selected.length !== 0 && tablePayload.selected.length !== tablePayload.assembledDataLength}
              />
            </div>
          }
          {/* {!tablePayload.stickyColumn && <div className='th-divider' style={{ marginTop: refs.current[0] && refs.current[0]?.offsetHeight / 5 }} />} */}
          {d.changeSize &&
            <div
              style={{ height: refs.current[2] ? refs.current[2].offsetHeight : 0 }}
              onMouseDown={(e) => {
                setActiveIndex(i)
                setInitialX(e.clientX)
                setInitialWidth(refs.current[i].offsetWidth)
              }}
              onTouchStart={(e) => {
                setActiveIndex(i)
                setInitialX(e.touches[0].clientX)
                setInitialWidth(refs.current[i].offsetWidth)
              }}
              className={`${d.sticky ? 'resize-handle sticky-handle' : 'resize-handle'} ${activeIndex === i ? 'active' : 'idle'}`}
            />
          }
          {(!d.sticky && !d.isOptionColumn) && <div className='th-divider' style={{
            height: refs.current[2] ? (refs.current[2]?.offsetHeight) / 2 : 0
          }}
          />}
        </div>
      ))}
    </div>
  </div>
}

TableHeaders.propTypes = {
  tablePayload: PropTypes.object,
  tableElement: PropTypes.object,
  activeColumns: PropTypes.array,
  UUIDCheckboxes: PropTypes.bool
}

export default TableHeaders
